import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import ProductCard from '../ProductCard/ProductCard';
import { fetchProducts, fetchCategories } from '../../utils/api';

const ORDER_OPTIONS = [
  { value: 'menu_order', label: 'Default' },
  { value: 'popularity', label: 'Best Sellers' },
  { value: 'rating',     label: 'Top Rated' },
  { value: 'date',       label: 'Newest' },
  { value: 'price',      label: 'Price: Low → High' },
  { value: 'price-desc', label: 'Price: High → Low' },
];

export default function FeaturedProducts({
  title       = 'Best Sellers ⭐',
  subtitle    = 'Top picks loved by kids!',
  limit       = 12,
  columns     = 6,
  categoryId  = '',
  orderby     = 'popularity',
  showFilters = true,
  showViewAll = true,
}) {
  const [activeCat,   setActiveCat]   = useState(categoryId || 'all');
  const [activeOrder, setActiveOrder] = useState(orderby);

  const { data: cats = [] } = useQuery({
    queryKey: ['product-categories'],
    queryFn:  () => fetchCategories({ per_page: 20, hide_empty: true }),
    staleTime: 5 * 60_000,
  });

  const { data: products = [], isLoading, isError } = useQuery({
    queryKey: ['products', activeCat, activeOrder, limit],
    queryFn:  () => {
      const params = { per_page: limit, orderby: activeOrder };
      if (activeOrder === 'price-desc') { params.orderby = 'price'; params.order = 'desc'; }
      if (activeCat !== 'all') params.category = activeCat;
      return fetchProducts(params);
    },
    staleTime: 2 * 60_000,
  });

  const viewAllUrl = activeCat !== 'all'
    ? `${window.themeData?.siteUrl ?? ''}/product-category/${cats.find((c) => String(c.id) === String(activeCat))?.slug ?? ''}/`
    : `${window.themeData?.siteUrl ?? ''}/shop/`;

  // Grid class based on columns prop
  const gridClass = {
    2: 'grid-cols-2',
    3: 'grid-cols-2 sm:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-4',
    6: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6',
  }[columns] ?? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6';

  const compact = columns >= 6;

  return (
    <section className="py-20 bg-gradient-to-b from-white to-primary/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="flex justify-between items-end mb-8">
          <div>
            <h2 className="text-4xl md:text-5xl font-black text-textDark mb-3">{title}</h2>
            {subtitle && <p className="text-xl text-textLight">{subtitle}</p>}
          </div>
          {showViewAll && (
            <a href={viewAllUrl} className="hidden md:flex items-center gap-2 text-primary font-bold hover:text-primaryHover transition-colors">
              View All <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => setActiveCat('all')}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${activeCat === 'all' ? 'bg-primary text-white' : 'bg-gray-100 text-textLight hover:bg-primary/10'}`}
            >
              All
            </button>
            {cats.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCat(String(cat.id))}
                className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${String(activeCat) === String(cat.id) ? 'bg-primary text-white' : 'bg-gray-100 text-textLight hover:bg-primary/10'}`}
              >
                {cat.name}
              </button>
            ))}
            <div className="ml-auto hidden md:flex items-center gap-2">
              <select
                value={activeOrder}
                onChange={(e) => setActiveOrder(e.target.value)}
                className="text-sm font-bold bg-gray-100 text-textLight border-none rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
              >
                {ORDER_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-20 gap-3 text-primary">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="font-semibold text-textLight">Loading products…</span>
          </div>
        )}

        {isError && (
          <div className="flex items-center justify-center py-16 gap-3 text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span className="font-semibold">Could not load products. Check WooCommerce is active.</span>
          </div>
        )}

        {!isLoading && !isError && (
          <div className={`grid ${gridClass} gap-4`}>
            {products.map((product, i) => (
              <ProductCard
                key={product.id}
                product={product}
                index={i}
                compact={compact}
              />
            ))}
          </div>
        )}

        {showViewAll && (
          <div className="mt-10 text-center md:hidden">
            <a href={viewAllUrl} className="inline-flex items-center gap-2 bg-primary text-white font-bold px-6 py-3 rounded-full hover:bg-primaryHover transition-colors">
              View All <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
